import React from 'react';
import { Content, Row, Col, Title, Text, Table } from 'components';
import { Button, Flex, Progress, Tag } from 'antd';
import { SideMenu } from '../components';
import Formatter from 'utils/Formatter';
import { stringToMoneyFormat } from 'utils';
import BusinessModal from './components/BusinessModal';
import Benefit from 'assets/grade_benefit.jpeg';

const MypageRatePresenter = (props) => {
  /* ===== Props ===== */
  const {
    screenSize,
    user,
    grades,
    totalPrice,
    totalCount,
    setting,
    isLoading,
    onSubmit,
  } = props;

  /* ===== Mutate ===== */

  /* ===== State ===== */
  const [isOpened, setIsOpened] = React.useState(false);
  const [modalData, setModalData] = React.useState(null);

  /* ===== Variables ===== */
  const styles = {
    titleLayout: {
      display: 'flex',
      gap: 8,
      alignItems: 'center',
      paddingBottom: 24,
      borderBottom: '2px solid var(--black-color)',
    },
    title: {
      fontSize: screenSize >= 768 ? 32 : screenSize >= 500 ? 28 : 20,
      fontWeight: 700,
      color: 'var(--black-color)',
    },
  };

  // NOTE: 테이블 컬럼
  const columns = [
    {
      key: 'grade',
      title: '멤버십 등급',
      align: 'start',
      fontWeight: 700,
      width: '15%',
    },
    {
      key: 'purchase',
      title: '기준',
      align: 'start',
      width: '35%',
    },
    {
      key: 'benefits',
      title: '등급 혜택',
      align: 'start',
      width: '50%',
    },
  ];

  /* ===== Function ===== */
  /* ===== Hooks ===== */
  /**
   * 등급 테이블 데이터 정제
   * --
   */
  const Grade = React.useMemo(() => {
    if (!setting) return;

    const { variable } = grades;
    const { total_month_standard, grade_standard } = setting;

    // 등급 기준 금액에 따른 내림차순 정렬
    const sortGrades = variable
      .slice()
      .sort((a, b) => b.grade_condition_amount - a.grade_condition_amount);

    const getPurchaseText = (grade, sub = '이상') => {
      if (!grade) return '';

      const { grade_condition_amount, transaction_count } = grade;
      const splitStandard = grade_standard.split('_');

      const text = splitStandard.reduce((acc, cur) => {
        switch (cur) {
          case 'AMOUNT':
            return (acc += `실 결제금액 ${stringToMoneyFormat(
              grade_condition_amount,
              '원'
            )} ${sub}`);
          case 'COUNT':
            return (acc += `실 구매건수 ${stringToMoneyFormat(
              transaction_count,
              '건'
            )} ${sub}`);
          case 'AND':
            return (acc += ', ');
          case 'OR':
            return (acc += ' 또는 ');
          default:
            return acc;
        }
      }, '');

      return text;
    };
    getPurchaseText();

    const newGrades = sortGrades?.map((grade, index) => ({
      key: `grade-${grade.grade_id}-${grade.grade_name}`,
      grade: grade.grade_name.toLocaleUpperCase(),
      purchase: `최근 ${total_month_standard}개월간 ${
        sortGrades.length - 1 === index
          ? getPurchaseText(sortGrades?.[index - 1], '미만')
          : getPurchaseText(grade)
      }`,
      benefits:
        !grade.coupon || !grade.coupon.length
          ? '-'
          : grade.coupon.map((coupon) => coupon.coupon_name).join(', '),
    }));

    return newGrades;
  }, [grades, setting]);

  /**
   * 다음달 예상 등급
   * --
   */
  const NextGrade = React.useMemo(() => {
    if (!setting)
      return {
        current: grades[0],
        next: grades[0],
      };

    const { grade_standard } = setting;
    const { variable } = grades;

    // 등급 기준 금액에 따른 내림차순 정렬
    const sortAmountGrades = variable
      .slice()
      .sort((a, b) => b.grade_condition_amount - a.grade_condition_amount);
    // 등급 기준 건수에 따른 내림차순 정렬
    const sortCountGrades = variable
      .slice()
      .sort((a, b) => b.transaction_count - a.transaction_count);

    let fid = 0,
      standard = sortAmountGrades;
    switch (grade_standard) {
      case 'AMOUNT':
        fid = sortAmountGrades.findIndex(
          (grade) => grade.grade_condition_amount <= totalPrice
        );
        break;
      case 'COUNT':
        standard = sortCountGrades;
        fid = sortCountGrades.findIndex(
          (grade) => grade.transaction_count <= totalCount
        );
        break;
      case 'AMOUNT_AND_COUNT':
        fid = sortAmountGrades.findIndex(
          (grade) =>
            grade.grade_condition_amount <= totalPrice &&
            grade.transaction_count <= totalCount
        );
        break;
      case 'AMOUNT_OR_COUNT':
        fid = sortAmountGrades.findIndex(
          (grade) =>
            grade.grade_condition_amount <= totalPrice ||
            grade.transaction_count <= totalCount
        );
        break;
      default:
        break;
    }

    return {
      current: standard[fid],
      next: fid === 0 ? null : standard[fid - 1],
    };
  }, [totalPrice, totalCount, grades, setting]);

  return (
    <React.Fragment>
      <Content
        maxWidth={1820}
        padding={screenSize >= 768 ? '120px 0px' : '60px 0'}
      >
        <Row gutter={25}>
          <Col x={0} md={1} xl={2}></Col>
          <Col x={0} md={6} xl={4}>
            <SideMenu />
          </Col>
          <Col x={24} md={16} xl={16}>
            <Content
              maxWidth={'100%'}
              padding={
                screenSize >= 1200
                  ? '0 0 0 95px'
                  : screenSize >= 768
                  ? '0 0 0 20px'
                  : '0'
              }
            >
              <Content maxWidth={'100%'} padding={0}>
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <Flex
                    style={{
                      ...styles.titleLayout,
                    }}
                    align="center"
                    justify="space-between"
                  >
                    <Title style={{ ...styles.title }}>회원 등급</Title>

                    {user?.business_level === 0 ? (
                      <Button onClick={() => setIsOpened(!isOpened)}>
                        사업자 신청하기
                      </Button>
                    ) : (
                      <Flex align="center" gap={4}>
                        <Tag
                          color={
                            user?.business_level === 1
                              ? 'processing'
                              : 'success'
                          }
                        >
                          사업자{' '}
                          {user?.business_level === 1 ? '심사중' : '인증 완료'}
                        </Tag>

                        <Button
                          onClick={() => {
                            setModalData({
                              businessNum: user?.business_num,
                              businessFile: user?.business_num_url,
                            });
                            setIsOpened(!isOpened);
                          }}
                        >
                          사업자 정보 수정
                        </Button>
                      </Flex>
                    )}
                  </Flex>

                  <div
                    style={{
                      backgroundColor: '#f4f4f4',
                      padding: screenSize >= 768 ? 40 : '40px 20px',
                      display: 'flex',
                      justifyContent:
                        screenSize >= 768 ? 'space-between' : 'unset',
                      marginBottom: 80,
                      color: 'var(--black-color)',
                      flexDirection: screenSize >= 768 ? 'unset' : 'column',
                      gap: screenSize >= 768 ? 0 : '20px',
                    }}
                  >
                    {/* 최상단 좌측 컨텐츠 시작 */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        width: screenSize >= 768 ? '50%' : '100%',
                      }}
                    >
                      <div
                        style={{
                          borderRadius: '50%',
                          border: '2px solid var(--black-color)',
                          backgroundColor: 'var(--white-color)',
                          width: screenSize >= 768 ? 100 : 80,
                          height: screenSize >= 768 ? 100 : 80,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginRight: screenSize >= 768 ? 28 : 10,
                        }}
                      >
                        <Text
                          size={screenSize >= 768 ? 28 : 20}
                          className={'dela-gothic'}
                          color={'var(--black-color)'}
                        >
                          {user?.grade?.grade_name?.[0]}
                        </Text>
                      </div>

                      <div
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          lineHeight: 1.75,
                        }}
                      >
                        <Text
                          size={screenSize >= 768 ? 24 : 18}
                          color={'var(--black-color)'}
                        >
                          {user?.user_name}
                          <span
                            style={{
                              fontSize: screenSize >= 768 ? 24 : 18,
                              color: 'var(--black-color)',
                            }}
                          >
                            님의 등급
                          </span>
                        </Text>
                        <Text
                          size={screenSize >= 768 ? 28 : 20}
                          className={'dela-gothic'}
                          color={'var(--black-color)'}
                        >
                          {user?.grade?.grade_name}
                        </Text>

                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'var(--black-color)',
                          }}
                        >
                          <Text
                            color={'var(--black-color)'}
                            size={screenSize >= 768 ? 16 : 14}
                          >
                            {Formatter.unixToDate(
                              user?.grade_start_date,
                              'YYYY.MM.DD'
                            )}
                          </Text>
                          <span
                            style={{
                              color: 'var(--black-color)',
                              fontSize: screenSize >= 768 ? 16 : 14,
                              padding: '0 5px',
                            }}
                          >
                            ~
                          </span>
                          <Text
                            color={'var(--black-color)'}
                            size={screenSize >= 768 ? 16 : 14}
                          >
                            {Formatter.unixToDate(
                              user?.grade_end_date,
                              'YYYY.MM.DD'
                            )}
                          </Text>
                        </div>
                      </div>
                    </div>
                    {/* 최상단 좌측 컨텐츠 끝 */}
                    {/* 최상단 우측 컨텐츠 */}
                    <div
                      style={{
                        width: screenSize >= 768 ? '50%' : '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between',
                        color: 'var(--black-color)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          color: 'var(--black-color)',
                        }}
                      >
                        <span
                          style={{
                            fontSize: 16,
                            paddingRight: 5,
                          }}
                        >
                          다음 달 예상 등급
                        </span>
                        <Text
                          style={{
                            color: 'var(--black-color)',
                            fontWeight: 700,
                          }}
                        >
                          {NextGrade.next
                            ? NextGrade.next?.grade_name
                            : NextGrade.current?.grade_name}
                        </Text>
                      </div>

                      {NextGrade.next ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'var(--black-color)',
                          }}
                        >
                          {[setting?.grade_standard].map((standard) => {
                            const remainPrice =
                              NextGrade.next.grade_condition_amount -
                              totalPrice;
                            const remainCount =
                              NextGrade.next.transaction_count - totalCount;

                            switch (standard) {
                              case 'AMOUNT':
                                return (
                                  <React.Fragment key={`setting-${standard}`}>
                                    <Text
                                      style={{
                                        color: 'var(--red-secondary-color)',
                                        fontWeight: 700,
                                        fontSize: screenSize >= 768 ? 16 : 15,
                                      }}
                                    >
                                      {stringToMoneyFormat(remainPrice)}
                                    </Text>
                                    <span
                                      style={{
                                        fontSize: screenSize >= 768 ? 16 : 15,
                                        paddingRight: 5,
                                        color: 'var(--red-secondary-color)',
                                        fontWeight: 700,
                                      }}
                                    >
                                      원
                                    </span>
                                    <span
                                      style={{
                                        fontSize: screenSize >= 768 ? 16 : 15,
                                        padding: '0 5px 1px 0',
                                        color: 'var(--black-color)',
                                      }}
                                    >
                                      추가 구매 시
                                    </span>
                                    <Text
                                      size={screenSize >= 768 ? 16 : 15}
                                      color={'var(--black-color)'}
                                    >
                                      {NextGrade.next?.grade_name}
                                    </Text>
                                    <span
                                      style={{
                                        fontSize: screenSize >= 768 ? 16 : 15,
                                        padding: '0 0 1px 5px',
                                        color: 'var(--black-color)',
                                      }}
                                    >
                                      달성
                                    </span>
                                  </React.Fragment>
                                );
                              case 'COUNT':
                                return (
                                  <React.Fragment key={`setting-${standard}`}>
                                    <Text
                                      style={{
                                        color: 'var(--red-secondary-color)',
                                        fontWeight: 700,
                                      }}
                                    >
                                      {stringToMoneyFormat(remainCount)}
                                    </Text>
                                    <span
                                      style={{
                                        fontSize: 16,
                                        paddingRight: 5,
                                        color: 'var(--red-secondary-color)',
                                        fontWeight: 700,
                                      }}
                                    >
                                      건
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 16,
                                        padding: '0 5px 1px 0',
                                        color: 'var(--black-color)',
                                      }}
                                    >
                                      추가 구매 시
                                    </span>
                                    <Text color={'var(--black-color)'}>
                                      {NextGrade.next?.grade_name}
                                    </Text>
                                    <span
                                      style={{
                                        fontSize: 16,
                                        padding: '0 0 1px 5px',
                                        color: 'var(--black-color)',
                                      }}
                                    >
                                      달성
                                    </span>
                                  </React.Fragment>
                                );
                              case 'AMOUNT_AND_COUNT':
                                return (
                                  <React.Fragment key={`setting-${standard}`}>
                                    <Text
                                      style={{
                                        color: 'var(--red-secondary-color)',
                                        fontWeight: 700,
                                      }}
                                    >
                                      {stringToMoneyFormat(
                                        remainPrice > 0 ? remainPrice : 0
                                      )}
                                    </Text>
                                    <span
                                      style={{
                                        fontSize: 16,
                                        paddingRight: 5,
                                        color: 'var(--red-secondary-color)',
                                        fontWeight: 700,
                                      }}
                                    >
                                      원,
                                    </span>
                                    <Text
                                      style={{
                                        color: 'var(--red-secondary-color)',
                                        fontWeight: 700,
                                      }}
                                    >
                                      {stringToMoneyFormat(
                                        remainCount > 0 ? remainCount : 0
                                      )}
                                    </Text>
                                    <span
                                      style={{
                                        fontSize: 16,
                                        paddingRight: 5,
                                        color: 'var(--red-secondary-color)',
                                        fontWeight: 700,
                                      }}
                                    >
                                      건
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 16,
                                        padding: '0 5px 1px 0',
                                        color: 'var(--black-color)',
                                      }}
                                    >
                                      추가 구매 시
                                    </span>
                                    <Text color={'var(--black-color)'}>
                                      {NextGrade.next?.grade_name}
                                    </Text>
                                    <span
                                      style={{
                                        fontSize: 16,
                                        padding: '0 0 1px 5px',
                                        color: 'var(--black-color)',
                                      }}
                                    >
                                      달성
                                    </span>
                                  </React.Fragment>
                                );
                              case 'AMOUNT_OR_COUNT':
                                return (
                                  <React.Fragment key={`setting-${standard}`}>
                                    <Text
                                      style={{
                                        color: 'var(--red-secondary-color)',
                                        fontWeight: 700,
                                      }}
                                    >
                                      {stringToMoneyFormat(remainPrice)}
                                    </Text>
                                    <span
                                      style={{
                                        fontSize: 16,
                                        paddingRight: 5,
                                        color: 'var(--red-secondary-color)',
                                        fontWeight: 700,
                                      }}
                                    >
                                      원
                                    </span>
                                    <Text
                                      style={{
                                        paddingRight: 5,
                                      }}
                                    >
                                      또는
                                    </Text>
                                    <Text
                                      style={{
                                        color: 'var(--red-secondary-color)',
                                        fontWeight: 700,
                                      }}
                                    >
                                      {' '}
                                      {stringToMoneyFormat(remainCount)}
                                    </Text>
                                    <span
                                      style={{
                                        fontSize: 16,
                                        paddingRight: 5,
                                        color: 'var(--red-secondary-color)',
                                        fontWeight: 700,
                                      }}
                                    >
                                      건
                                    </span>
                                    <span
                                      style={{
                                        fontSize: 16,
                                        padding: '0 5px 1px 0',
                                        color: 'var(--black-color)',
                                      }}
                                    >
                                      추가 구매 시
                                    </span>
                                    <Text color={'var(--black-color)'}>
                                      {NextGrade.next?.grade_name}
                                    </Text>
                                    <span
                                      style={{
                                        fontSize: 16,
                                        padding: '0 0 1px 5px',
                                        color: 'var(--black-color)',
                                      }}
                                    >
                                      달성
                                    </span>
                                  </React.Fragment>
                                );
                              default:
                                <React.Fragment key={`setting-${standard}`}>
                                  <Text
                                    style={{
                                      color: 'var(--red-secondary-color)',
                                      fontWeight: 700,
                                    }}
                                  >
                                    -
                                  </Text>
                                </React.Fragment>;
                            }
                            return (
                              <React.Fragment key={`setting-${standard}`}>
                                <Text
                                  style={{
                                    color: 'var(--red-secondary-color)',
                                    fontWeight: 700,
                                  }}
                                >
                                  -
                                </Text>
                              </React.Fragment>
                            );
                          })}
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'var(--black-color)',
                          }}
                        >
                          <Text
                            style={{
                              color: 'var(--red-secondary-color)',
                              fontWeight: 700,
                            }}
                          >
                            최고등급달성!
                          </Text>
                        </div>
                      )}

                      {NextGrade.next ? (
                        [setting?.grade_standard].map((standard) => {
                          const remainPrice =
                            (totalPrice /
                              NextGrade.next.grade_condition_amount) *
                            100;
                          const remainCount =
                            (totalCount / NextGrade.next.transaction_count) *
                            100;

                          switch (standard) {
                            case 'AMOUNT':
                              return (
                                <Progress
                                  key={`amount-progress`}
                                  percent={remainPrice}
                                  showInfo={false}
                                  strokeColor={'#111111'}
                                  trailColor={'#D9D9D9'}
                                />
                              );
                            case 'COUNT':
                              return (
                                <Progress
                                  key={`count-progress`}
                                  percent={remainCount}
                                  showInfo={false}
                                  strokeColor={'#111111'}
                                  trailColor={'#D9D9D9'}
                                />
                              );
                            case 'AMOUNT_AND_COUNT':
                              const avg = (remainPrice + remainCount) / 2;

                              return (
                                <Progress
                                  key={`amount-and-count-progress`}
                                  percent={avg}
                                  showInfo={false}
                                  strokeColor={'#111111'}
                                  trailColor={'#D9D9D9'}
                                />
                              );
                            case 'AMOUNT_OR_COUNT':
                              const per =
                                remainPrice > remainCount
                                  ? remainPrice
                                  : remainCount;

                              return (
                                <Progress
                                  key={`amount-or-count-progress`}
                                  percent={per}
                                  showInfo={false}
                                  strokeColor={'#111111'}
                                  trailColor={'#D9D9D9'}
                                />
                              );
                            default:
                              return (
                                <Progress
                                  key={'default-progress'}
                                  percent={100}
                                  showInfo={false}
                                  strokeColor={'#111111'}
                                  trailColor={'#D9D9D9'}
                                />
                              );
                          }
                        })
                      ) : (
                        <Progress
                          percent={100}
                          showInfo={false}
                          strokeColor={'#111111'}
                          trailColor={'#D9D9D9'}
                        />
                      )}

                      {NextGrade.next ? (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                          }}
                        >
                          <Text color={'var(--black-color)'}>
                            {NextGrade.current.grade_name}
                          </Text>
                          <Text color={'var(--black-color)'}>
                            {NextGrade.next.grade_name}
                          </Text>
                        </div>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                          }}
                        >
                          <Text color={'var(--black-color)'}>
                            {NextGrade.current?.grade_name}
                          </Text>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* 멤버십 등급 혜택 */}
                <div
                  style={{
                    ...styles.titleLayout,
                  }}
                >
                  <Title
                    style={{
                      ...styles.title,
                      fontSize: screenSize >= 768 ? 24 : 18,
                    }}
                  >
                    멤버십 등급 혜택
                  </Title>
                </div>
                <div style={{ width: '100%' }}>
                  <img src={Benefit} alt="등급혜택" width={'100%'} />
                </div>
                {/* <Table
                  data={Grade}
                  pagination={false}
                  columns={columns}
                  minHeight={'auto'}
                  minWidth={'auto'}
                  bordered={true}
                  hovered={true}
                  //layoutStyle={{ margin: '20px 0' }}
                  headerStyle={{
                    backgroundColor: 'var(--white-color)',
                    fontWeight: '700',
                    fontSize: screenSize >= 768 ? 16 : 14,
                    borderBottom: '1px solid var(--black-color)',
                    textAlign: 'start',
                    padding: '16px 40px',
                    wordBreak: 'keep-all',
                    whiteSpace: 'nowrap',
                  }}
                  bodyRowStyle={{
                    padding: '16px 40px',
                    fontSize: screenSize >= 768 ? 16 : 14,
                  }}
                  empty={<div>데이터가 없습니다.</div>}
                /> */}
                {/* 멤버십 등급 혜택 끝 */}

                {/* <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    marginTop: 80,
                    padding: '16px 0',
                    color: 'var(--black-color)',
                    lineHeight: 1.75,
                  }}
                >
                  <span
                    style={{
                      marginBottom: 16,
                      fontWeight: 700,
                    }}
                  >
                    멤버십 등급 기준 안내
                  </span>
                  <span>
                    ∙ 최근 {setting?.total_month_standard}개월 동안 구매 확정된
                    누적 실 결제금액을 기준으로 매월
                    {setting?.date_of_change}일 새로운 등급이 부여됩니다.
                  </span>
                  <span>
                    ∙ 실 결제금액은 포인트, 쿠폰 등 할인 금액을 제외한 결제금액
                    입니다.
                  </span>
                  <span>
                    ∙ 예상 등급이란 멤버십 산정 기준에 따라 익월{' '}
                    {setting?.date_of_change}일 반영 예정인 등급입니다.
                  </span>
                  <span>
                    ∙ 멤버십 혜택 및 산정 기준은 당사의 사정에 의해 변경될 수
                    있습니다.
                  </span>
                  <span>
                    ∙ 온라인과 오프라인 구매 실적 포인트는 제품별 상이합니다.
                  </span>
                </div>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: '16px 0',
                    color: 'var(--black-color)',
                    lineHeight: 1.75,
                  }}
                >
                  <span
                    style={{
                      marginBottom: 16,
                      fontWeight: 700,
                    }}
                  >
                    멤버십 혜택 안내
                  </span>
                  <span>
                    ∙ 등급별로 혜택이 상이하며, 모든 등급쿠폰은 매월 1일부터
                    말일까지 사용 가능합니다.
                  </span>
                  <span>
                    ∙ 무료배송 쿠폰은 GOLD 등급 이상 고객님께만 발급되며,
                    제이엠커피 자사브랜드에 한해 적용 가능합니다.
                  </span>
                  <span>∙ 유효기간이 만료된 쿠폰은 재발급이 불가능합니다.</span>
                  <span>
                    ∙ 상품 구매 포인트는 상품 구매 확정시 실 결제금액 기준으로
                    지급되며, 브랜드/제품별로 포인트 적립률이 상이합니다.
                  </span>
                  <span>
                    ∙ 장바구니쿠폰은 결제 금액별 적용 가능쿠폰이 상이합니다.
                  </span>
                  <span>
                    ∙ 제이엠 자사브랜드: 최대 1.0%, 입점사: 최대 0.5%, 오프라인
                    구매 실적 최대 1.0%
                  </span>
                  <span>∙ 행사제품 면세제품은 적립 제외</span>
                  <span>∙ 모든 쿠폰은 중복사용 불가</span>
                  <span>
                    ∙ 매장제조/베이커리 상품은 포인트지급이 되지 않습니다.
                  </span>
                  <span>
                    ∙ 매장제조음료 상품은 스탬프지급 / 10잔 이용시 1잔 무료
                    아메리카노 교환 쿠폰 발행
                  </span>
                </div> */}
              </Content>
            </Content>
          </Col>
          <Col x={0} md={1} xl={2}></Col>
        </Row>
      </Content>

      <BusinessModal
        isOpened={isOpened}
        onClose={() => setIsOpened(!isOpened)}
        isLoading={isLoading}
        onSubmit={onSubmit}
        defaultData={modalData}
      />
    </React.Fragment>
  );
};

export default MypageRatePresenter;
